import { Home, ImageIcon } from 'lucide-react'
import { useState } from 'react'

import { ImageLightbox } from '@components/image/ImageLightbox'
import { cn } from '@/lib/utils'

interface ApartmentGalleryProps {
  images: string[]
  title?: string
  /** Single-hero variant with a photo-count badge, for narrow containers. */
  compact?: boolean
}

function GalleryTile({
  src,
  alt,
  onClick,
  className,
  overlayCount
}: {
  src?: string
  alt?: string
  onClick?: () => void
  className?: string
  overlayCount?: number
}) {
  const interactive = Boolean(src) && Boolean(onClick)

  return (
    <button
      type="button"
      onClick={interactive ? onClick : undefined}
      disabled={!interactive}
      className={cn(
        'group relative block appearance-none overflow-hidden border-0 p-0 bg-ink/[0.06]',
        interactive ? 'cursor-zoom-in' : 'cursor-default',
        className
      )}
    >
      {src ? (
        <img
          src={src}
          alt={alt ?? ''}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
        />
      ) : (
        <span className="absolute inset-0 flex items-center justify-center text-ink/25">
          <Home className="h-8 w-8" />
        </span>
      )}

      {!!overlayCount && overlayCount > 0 && (
        <span className="absolute inset-0 flex items-center justify-center bg-ink/60 text-xl font-semibold text-white backdrop-blur-[1px]">
          +{overlayCount}
        </span>
      )}
    </button>
  )
}

export function ApartmentGallery({
  images,
  title,
  compact = false
}: ApartmentGalleryProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  const hasImages = images.length > 0
  const remaining = Math.max(0, images.length - 5)

  const open = (index: number) => {
    if (!hasImages) return
    setLightboxIndex(Math.min(index, images.length - 1))
  }

  const lightbox = lightboxIndex !== null && hasImages && (
    <ImageLightbox
      images={images}
      index={lightboxIndex}
      onClose={() => setLightboxIndex(null)}
      onIndexChange={setLightboxIndex}
    />
  )

  if (compact) {
    return (
      <>
        <button
          type="button"
          onClick={() => open(0)}
          disabled={!hasImages}
          className={cn(
            'group relative block aspect-[16/10] w-full appearance-none overflow-hidden rounded-2xl border-0 p-0 bg-ink/[0.06]',
            hasImages ? 'cursor-zoom-in' : 'cursor-default'
          )}
        >
          {images[0] ? (
            <img
              src={images[0]}
              alt={title ?? ''}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            />
          ) : (
            <span className="absolute inset-0 flex items-center justify-center text-ink/25">
              <Home className="h-8 w-8" />
            </span>
          )}
          {images.length > 1 && (
            <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-ink/70 px-2.5 py-1 text-xs font-medium text-white">
              <ImageIcon className="h-3.5 w-3.5" />
              {images.length}
            </span>
          )}
        </button>
        {lightbox}
      </>
    )
  }

  return (
    <>
      <div className="grid h-[300px] grid-cols-4 grid-rows-2 gap-2 sm:h-[440px]">
        <GalleryTile
          src={images[0]}
          alt={title}
          onClick={() => open(0)}
          className="col-span-4 row-span-2 rounded-2xl sm:col-span-2"
        />
        {[1, 2, 3, 4].map((pos) => (
          <GalleryTile
            key={pos}
            src={images[pos]}
            alt={title}
            onClick={() => open(pos)}
            className="hidden rounded-2xl sm:block"
            overlayCount={pos === 4 ? remaining : 0}
          />
        ))}
      </div>

      {lightbox}
    </>
  )
}
